import React, { forwardRef, useCallback } from 'react';
import { clsx } from 'clsx';
import styles from './Button.module.scss';
import { LoaderIcon } from '../../icons/Icon';
import {
  eventTracker,
  type TrackEventProps,
} from '../../../components/EventTracker';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Button variant style
   */
  variant?: 'filled' | 'outlined' | 'text' | 'tonal' | 'outlined-gradient' | 'link-row';

  /**
   * Button size
   */
  size?: 'xsmall' | 'small' | 'medium' | 'large';

  /**
   * Button color theme
   */
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'success';

  /**
   * Whether the button is in a loading state
   */
  loading?: boolean;

  /**
   * Whether the button is disabled
   */
  disabled?: boolean;

  /**
   * Icon to display on the left side of the button
   */
  leftIcon?: React.ReactNode;

  /**
   * Icon to display on the right side of the button
   */
  rightIcon?: React.ReactNode;

  /**
   * Whether the button should only show an icon (no text)
   */
  iconOnly?: boolean;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Additional CSS class name for the button content
   */
  contentClassName?: string;

  /**
   * Button text content
   */
  text?: string;

  /**
   * Button content
   */
  children?: React.ReactNode;

  /**
   * Stable identifier used to track clicks via the event tracker.
   * When omitted, no tracking event is fired.
   */
  trackingId?: string;

  /**
   * Extra properties attached to the tracked click event
   */
  trackingProps?: TrackEventProps;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'filled',
      size = 'medium',
      color = 'primary',
      loading = false,
      disabled = false,
      leftIcon,
      rightIcon,
      iconOnly = false,
      className,
      text,
      children,
      contentClassName,
      trackingId,
      trackingProps,
      onClick,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        if (trackingId) {
          eventTracker.trackClick(trackingId, trackingProps);
        }
        onClick?.(event);
      },
      [trackingId, trackingProps, onClick]
    );

    const buttonClasses = clsx(
      styles.root,
      styles[`variant--${variant}`],
      styles[`size--${size}`],
      styles[`color--${color}`],
      {
        [styles.loading]: loading,
        [styles.disabled]: isDisabled,
        [styles.iconOnly]: iconOnly,
      },
      className
    );

    return (
      <button
        ref={ref}
        className={buttonClasses}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        onClick={handleClick}
        {...props}
      >
        {loading && (
          <span className={styles.loader} aria-hidden="true">
            <LoaderIcon className={styles.spinner} />
          </span>
        )}

        {!loading && leftIcon && (
          <span className={styles.leftIcon} aria-hidden="true">
            {leftIcon}
          </span>
        )}

        {!loading && text && !iconOnly && (
          <span className={styles.text}>{text}</span>
        )}

        {!loading && !text && children && !iconOnly && (
          <span className={clsx(styles.content, contentClassName)}>
            {children}
          </span>
        )}

        {!loading && rightIcon && (
          <span className={styles.rightIcon} aria-hidden="true">
            {rightIcon}
          </span>
        )}
      </button>
    );
  }
);
